// ============================================================
// TaskFlow OS - Auth Store (Zustand)
// ============================================================

import { create } from 'zustand';
import type { Profile } from '../lib/types';
import { supabase, signIn, signUp, signOut as authSignOut } from '../lib/supabase';

interface AuthState {
  user: Profile | null;
  loading: boolean;
  initialized: boolean;

  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<{ error: string | null }>;
  register: (email: string, password: string, displayName: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: string | null }>;
  fetchProfile: (userId: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  initialized: false,

  initialize: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await get().fetchProfile(session.user.id);
      }
    } catch (err) {
      console.error('Auth init error:', err);
    } finally {
      set({ loading: false, initialized: true });
    }

    supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        await get().fetchProfile(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        set({ user: null });
      }
    });
  },

  login: async (email, password) => {
    set({ loading: true });
    const { error } = await signIn(email, password);
    set({ loading: false });
    if (error) return { error: error.message };
    return { error: null };
  },

  register: async (email, password, displayName) => {
    set({ loading: true });
    const { error } = await signUp(email, password, displayName);
    set({ loading: false });
    if (error) return { error: error.message };
    return { error: null };
  },

  logout: async () => {
    await authSignOut();
    set({ user: null });
  },

  updateProfile: async (updates) => {
    const currentUser = get().user;
    if (!currentUser) return { error: '未ログイン' };

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', currentUser.id);

    if (error) return { error: error.message };

    set({ user: { ...currentUser, ...updates } });
    return { error: null };
  },

  fetchProfile: async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (!error && data) {
      set({ user: data as Profile });
    }
  },
}));
